import haversine from 'haversine-distance';
import {
  getExactCoordinateKey,
  getPhotoPosition,
  getSpotCandidates,
} from './photoModel';

// The map shows more than the model was given: every spot already created
// around the photo, so it is obvious whether the area is covered.
export const MAP_SPOT_RADIUS_METERS = 1500;
export const MAX_MAP_SPOTS = 40;

export const getSpotsAround = (spots, photo, radiusMeters = MAP_SPOT_RADIUS_METERS) => {
  const position = getPhotoPosition(photo);

  if (!position || !Array.isArray(spots)) {
    return [];
  }

  return spots
    .map(spot => {
      const spotPosition = getPhotoPosition(spot);

      return spotPosition
        ? { ...spot, distanceMeters: haversine(position, spotPosition) }
        : null;
    })
    .filter(spot => spot && spot.distanceMeters <= radiusMeters)
    .sort((left, right) => left.distanceMeters - right.distanceMeters)
    .slice(0, MAX_MAP_SPOTS);
};

// The whole spot universe is fetched once per session on the frontend, so each
// suggestion only tells the backend which ids are worth re-reading. The backend
// still re-reads every spot and recomputes the distance itself.
//
// One fixed radius cannot serve both a city and a nature reserve. In a city the
// nearest spots are metres away and anything further is noise; in open country
// the only spot that matters can be kilometres off, and sending nothing at all
// makes the model invent a duplicate of a spot that already exists.
//
// So the radius grows until there are enough spots to choose from, and stops the
// moment there are. A dense place never leaves the first step; an empty one
// reaches the last and returns the two or three spots that exist there.
export const CANDIDATE_RADIUS_LADDER = Object.freeze([500, 1500, 3000, 5000]);
// Must not be lower than the twelve the backend actually shows the model. It was
// eight, so the ladder stopped at the first radius holding eight spots and left
// four slots empty: in Cambridge ten spots inside 500 m ended the search, and
// "River Cam" at 571 m was never offered on photos of the river.
export const ENOUGH_CANDIDATE_SPOTS = 12;
export const MAX_CANDIDATE_SPOT_IDS = 20;

export const getCandidateSpotIds = (spotIndex, photo) => {
  if (!spotIndex || !photo) {
    return [];
  }

  let found = [];

  for (let step = 0; step < CANDIDATE_RADIUS_LADDER.length; step += 1) {
    const radius = CANDIDATE_RADIUS_LADDER[step];

    found = getSpotCandidates(spotIndex, photo, radius, radius);

    if (found.length >= ENOUGH_CANDIDATE_SPOTS) {
      break;
    }
  }

  return found
    .map(candidate => String((candidate && candidate.id) || '').trim())
    .filter(Boolean)
    .slice(0, MAX_CANDIDATE_SPOT_IDS);
};

// OpenAI hands out complimentary tokens per day per tier, not per model: every
// SOL model draws on one allowance and every TERRA model on another, counting
// the input and the output alike, and resetting at UTC midnight. Usage is
// therefore counted per tier, so moving from gpt-6-sol to gpt-6.1-sol mid-day
// keeps what the day has already spent. Auto Mod spends the strong tier first
// and falls back to the cheap one, so the roster is ordered best-first. The
// limits are the tier 1-2 figures and will change: they live here so there is
// one place to edit.
export const MODEL_ROSTER = Object.freeze([
  Object.freeze({
    id: 'gpt-6.1-sol',
    tier: 'sol',
    label: 'SOL',
    dailyTokenLimit: 250000,
    colour: '#ffd34d',
  }),
  Object.freeze({
    id: 'gpt-5.6-terra',
    tier: 'terra',
    label: 'TERRA',
    dailyTokenLimit: 2500000,
    colour: '#8ce563',
  }),
]);

// Which allowance a model draws on. A model that has left the roster - the one
// it replaced - still belongs to its tier, which its name ends in.
export const getModelTier = (modelId, roster = MODEL_ROSTER) => {
  const id = String(modelId || '').trim();
  const listed = roster.find(model => model.id === id);

  if (listed) {
    return listed.tier;
  }

  const byName = roster.find(model => id.endsWith(`-${model.tier}`));

  return byName ? byName.tier : id;
};

export const TOKEN_USAGE_STORAGE_KEY = 'autoModerationTokenUsage';
// Measured on 30 September 2026 over the last 800 suggestions. A user photo with
// nothing nearby costs about 15k tokens (16.5k at the ninetieth percentile), an
// Unsplash or Detective photo about 12k, and every candidate spot adds roughly
// 650-700 more: its three low-detail images and its details. The estimate is the
// dearest queue at the ninetieth percentile, because it is what decides whether
// the last request of the day still fits the complimentary allowance, and an
// estimate that runs short tips that request into paid tokens. It used to say
// 7.5k, from before the prompt grew. The backend attaches at most twelve
// candidates.
export const BASE_REQUEST_TOKENS = 16500;
export const TOKENS_PER_CANDIDATE = 700;
export const MAX_ATTACHED_CANDIDATES = 12;

export const estimateRequestTokens = candidateCount => {
  const attached = Math.min(Math.max(Number(candidateCount) || 0, 0), MAX_ATTACHED_CANDIDATES);

  return BASE_REQUEST_TOKENS + attached * TOKENS_PER_CANDIDATE;
};

export const getUsageDayKey = (now = new Date()) => now.toISOString().slice(0, 10);

export const getEmptyUsage = (day = getUsageDayKey()) => ({ day, byTier: {} });

// Anything from another UTC day is gone: the allowance reset with it. A day
// stored per model, before usage was counted per tier, is folded into its tiers.
export const readStoredUsage = (storage, day = getUsageDayKey()) => {
  try {
    const stored = JSON.parse(storage.getItem(TOKEN_USAGE_STORAGE_KEY) || 'null');

    if (!stored || stored.day !== day) {
      return getEmptyUsage(day);
    }

    if (stored.byTier) {
      return { day, byTier: { ...stored.byTier } };
    }

    return Object.entries(stored.byModel || {}).reduce(
      (usage, [model, totalTokens]) => addUsage(usage, { model, totalTokens }, day),
      getEmptyUsage(day)
    );
  } catch (error) {
    return getEmptyUsage(day);
  }
};

export const writeStoredUsage = (storage, usage) => {
  try {
    storage.setItem(TOKEN_USAGE_STORAGE_KEY, JSON.stringify(usage));
  } catch (error) {
    // A full or unavailable localStorage must never stop the moderation queue.
  }
};

export function addUsage(usage, tokenUsage, day = getUsageDayKey()) {
  const spent = Number(tokenUsage && tokenUsage.totalTokens) || 0;
  const tier = getModelTier(tokenUsage && tokenUsage.model);
  const base = usage && usage.day === day && usage.byTier ? usage : getEmptyUsage(day);

  if (!spent || !tier) {
    return base;
  }

  return {
    day,
    byTier: { ...base.byTier, [tier]: (base.byTier[tier] || 0) + spent },
  };
}

// What the backend stored as spent today, laid over what this browser counted.
// The nightly run - and a session in another browser - spends tokens this page
// never saw land, so each tier takes the larger of the two figures: nearly
// everything counted here was stored there too, and adding them would count it
// twice.
export function withServerUsage(usage, serverUsage, day = getUsageDayKey()) {
  const base = usage && usage.day === day && usage.byTier ? usage : getEmptyUsage(day);

  if (!serverUsage || serverUsage.day !== day || !serverUsage.byTier) {
    return base;
  }

  const byTier = { ...base.byTier };

  Object.entries(serverUsage.byTier).forEach(([tier, tokens]) => {
    const spent = Number(tokens) || 0;

    if (spent > (byTier[tier] || 0)) {
      byTier[tier] = spent;
    }
  });

  return { day, byTier };
}

// The allowance resets at 00:00 UTC whether or not the page reloaded, so a
// counter carried over from yesterday reads as zero. Without this the first
// request after midnight would still be sent to the fallback model, and the bars
// would show yesterday's fill until something landed to clear them.
export const getUsageForToday = (usage, day = getUsageDayKey()) =>
  usage && usage.day === day ? usage : getEmptyUsage(day);

export const getModelUsage = (usage, modelId, day = getUsageDayKey()) => {
  const today = getUsageForToday(usage, day);

  const tier = getModelTier(modelId);

  return Number((today.byTier && today.byTier[tier]) || 0);
};

const canAfford = (usage, model, cost, day) =>
  model.dailyTokenLimit - getModelUsage(usage, model.id, day) >= cost;

// Whether one particular model's allowance can still take a request of this
// size. The bench asks this of the fallback model alone, so it never spends the
// strong model's allowance the live queue is using first.
export const hasRoomOn = (usage, modelId, cost, roster = MODEL_ROSTER, day = getUsageDayKey()) => {
  const model = roster.find(item => item.id === modelId);

  return Boolean(model) && canAfford(usage, model, cost, day);
};

// Best model whose allowance still has room for this particular request. The
// cost is estimated per photo, so a photo with no nearby spots keeps going to
// the strong model after an expensive one would already have been pushed to the
// fallback. When every allowance is spent the last model is used anyway: the
// owner watches the bars and stops moderating, the page does not decide that.
export const getAffordableModel = (
  usage,
  candidateCount = MAX_ATTACHED_CANDIDATES,
  roster = MODEL_ROSTER,
  day = getUsageDayKey()
) => {
  const cost = estimateRequestTokens(candidateCount);
  const affordable = roster.find(model => canAfford(usage, model, cost, day));

  return affordable ? affordable.id : null;
};

export const pickModel = (
  usage,
  candidateCount = MAX_ATTACHED_CANDIDATES,
  roster = MODEL_ROSTER,
  day = getUsageDayKey()
) => getAffordableModel(usage, candidateCount, roster, day) || roster[roster.length - 1].id;

// The last model in the roster is the one everything falls back to. Its
// allowance is large enough to ask for photos in parallel; every model before it
// is asked one photo at a time, so each choice is made on what was really spent.
export const isFallbackModel = (modelId, roster = MODEL_ROSTER) =>
  modelId === roster[roster.length - 1].id;

// Past the free allowance every suggestion is paid for, so looking ahead stops:
// a preloaded photo that never gets decided is money spent on nothing. The
// cheapest possible photo is the test - while one of those still fits, the day
// is not over.
export const hasComplimentaryTokensLeft = (usage, roster = MODEL_ROSTER, day = getUsageDayKey()) =>
  roster.some(model => canAfford(usage, model, estimateRequestTokens(0), day));

// Subscribers were promised their photos are moderated first, so the queue is
// ordered by tier and everything inside a tier keeps the order it arrived in.
export const SUBSCRIPTION_PRIORITY = Object.freeze({ gold: 0, silver: 1, bronze: 2 });
export const UNSUBSCRIBED_PRIORITY = 3;

export const getSubscriptionLevels = users =>
  new Map(
    (Array.isArray(users) ? users : [])
      .filter(user => user && user.id && SUBSCRIPTION_PRIORITY[user.subscriptionLevel] != null)
      .map(user => [String(user.id), user.subscriptionLevel])
  );

export const getPhotoPriority = (photo, levels) => {
  const userId = String((photo && photo.user && photo.user.id) || '');
  const priority = SUBSCRIPTION_PRIORITY[levels instanceof Map ? levels.get(userId) : undefined];

  return priority == null ? UNSUBSCRIBED_PRIORITY : priority;
};

export const sortQueueBySubscription = (photos, levels) => {
  const list = Array.isArray(photos) ? photos : [];

  return list
    .map((photo, index) => ({ photo, index, priority: getPhotoPriority(photo, levels) }))
    .sort((left, right) => left.priority - right.priority || left.index - right.index)
    .map(entry => entry.photo);
};

// A suggestion is built against the spots that existed when it was requested, so
// preloading a photo taken near the current one produces a stale answer: it is
// told to create a spot that the photo in front of it is about to create. Those
// neighbours are pushed to the back of the queue instead, and by the time they
// come round the spot exists and they can be assigned to it.
export const PRELOAD_COUNT = 20;
export const PRELOAD_MIN_DISTANCE_METERS = 1000;

export const getPreloadPlan = (photos, count = PRELOAD_COUNT) => {
  const list = Array.isArray(photos) ? photos : [];
  const anchors = [];
  const preload = [];
  const deferred = [];

  if (list.length === 0) {
    return { preload, deferred };
  }

  const currentPosition = getPhotoPosition(list[0]);

  if (currentPosition) {
    anchors.push(currentPosition);
  }

  for (let index = 1; index < list.length && preload.length < count; index += 1) {
    const photo = list[index];
    const position = getPhotoPosition(photo);
    // A photo with no usable coordinates cannot collide with anything.
    const isNeighbour =
      position && anchors.some(anchor => haversine(anchor, position) < PRELOAD_MIN_DISTANCE_METERS);

    if (isNeighbour) {
      deferred.push(photo);
    } else {
      preload.push(photo);

      if (position) {
        anchors.push(position);
      }
    }
  }

  // Reordering only pays off when something further away was found to take the
  // freed slot. When every photo left is a neighbour, the queue stays as it is.
  return preload.length > 0 ? { preload, deferred } : { preload, deferred: [] };
};

// A neighbour goes to the back of its own subscription tier, never behind a
// lower one: waiting for a spot to exist must not cost a subscriber their place.
// Returns the same array when nothing actually moves - a neighbour already at
// the back of its tier does not need to be sent there again, and re-setting an
// identically ordered queue would loop the effect that calls this forever.
export const getQueueWithDeferredLast = (photos, deferred, levels) => {
  if (!Array.isArray(photos) || !Array.isArray(deferred) || deferred.length === 0) {
    return photos;
  }

  const deferredIds = new Set(deferred.map(photo => photo.id));
  const reordered = photos.filter(photo => !deferredIds.has(photo.id));

  deferred.forEach(photo => {
    const priority = getPhotoPriority(photo, levels);
    // Index 0 is the photo being decided right now and never moves.
    const insertAt = reordered.findIndex(
      (item, index) => index > 0 && getPhotoPriority(item, levels) > priority
    );

    if (insertAt === -1) {
      reordered.push(photo);
    } else {
      reordered.splice(insertAt, 0, photo);
    }
  });

  const hasMoved = reordered.some((photo, index) => photo.id !== photos[index].id);

  return hasMoved ? reordered : photos;
};

// Grouped photos are the one case where deciding photo by photo produces
// duplicate spots, so Auto Mod hands the whole group to /moderation instead.
export const isGroupedPhoto = photo => {
  const spotGroup = (photo && photo.spotGroup) || null;

  return Boolean(
    spotGroup && spotGroup.id && Number(spotGroup.expectedMemberCount || 0) > 1
  );
};

export const splitGroupedPhotos = (photos, spotIndex = null) => {
  const list = Array.isArray(photos) ? photos : [];
  const coordinateCounts = new Map();

  list.forEach(photo => {
    const key = getExactCoordinateKey(photo);

    if (key) {
      coordinateCounts.set(key, (coordinateCounts.get(key) || 0) + 1);
    }
  });

  const isGrouped = photo => {
    const key = getExactCoordinateKey(photo);

    return isGroupedPhoto(photo) || (key ? coordinateCounts.get(key) > 1 : false);
  };

  // A cluster sitting on a spot that already exists is not a duplication risk:
  // the answer for each of its photos is almost always "assign to that spot", so
  // they go through the normal queue instead of being skipped.
  const isCovered = photo =>
    Boolean(spotIndex) && getCandidateSpotIds(spotIndex, photo).length > 0;

  return {
    single: list.filter(photo => !isGrouped(photo) || isCovered(photo)),
    grouped: list.filter(photo => isGrouped(photo) && !isCovered(photo)),
  };
};

// The photos of one cluster, keyed by what makes them a cluster, so each group
// can be ordered on its own.
export const getPhotoGroups = photos => {
  const groups = new Map();

  (Array.isArray(photos) ? photos : []).forEach(photo => {
    const key =
      (photo.spotGroup && photo.spotGroup.id) || getExactCoordinateKey(photo) || photo.id;

    groups.set(key, [...(groups.get(key) || []), photo]);
  });

  return [...groups.values()];
};

// The representative is judged first so it is the one that creates the spot and
// becomes its main photo; everything else in the cluster then has something to
// be assigned to.
export const withRepresentativeFirst = (group, representativeId) => {
  const index = group.findIndex(photo => photo.id === representativeId);

  return index <= 0 ? group : [group[index], ...group.filter((_, i) => i !== index)];
};

// How much the submitted coordinates can be trusted, and the colour that says so
// on the map marker and next to the proposal.
const GPS_TRUST_BY_KIND = Object.freeze({
  native: {
    level: 'trusted',
    color: '#d9ff72',
    summary: 'From the camera',
  },
  manual_correction: {
    level: 'edited',
    color: '#63d2ff',
    summary: 'Camera GPS, refined by the uploader',
  },
  manual_placement: {
    level: 'manual',
    color: '#ffb454',
    summary: 'Placed by hand, no camera GPS',
  },
  unknown: {
    level: 'unknown',
    color: '#9aa39a',
    summary: 'The app never recorded how this point was chosen',
  },
  // A Detective point was never recorded by a camera: somebody who recognised
  // the photo tapped a map. Agreement between strangers is the only thing that
  // makes it trustworthy, which is why the summary counts them.
  detective: {
    level: 'detective',
    color: '#c69bff',
    summary: 'Placed from the app by people who recognised the photo',
  },
  missing: {
    level: 'missing',
    color: '#ff756b',
    summary: 'No usable coordinates',
  },
});

export const getGpsTrust = provenance => {
  const kind = (provenance && provenance.kind) || 'unknown';
  const trust = GPS_TRUST_BY_KIND[kind] || GPS_TRUST_BY_KIND.unknown;

  return {
    ...trust,
    kind,
    label: (provenance && provenance.label) || 'Location source unknown',
    detail: (provenance && provenance.detail) || '',
    correctionDistanceMeters:
      provenance && Number.isFinite(Number(provenance.correctionDistanceMeters))
        ? Number(provenance.correctionDistanceMeters)
        : null,
    originalGps: (provenance && provenance.originalGps) || null,
    selectedGps: (provenance && provenance.selectedGps) || null,
  };
};

// A Detective photo has no camera GPS to compare against, so the trust line
// reports who found it instead: how many people answered, how many agreed on the
// point being used, and whether they disagree.
export const getDetectiveProvenance = photo => {
  const standpoints = (photo && Array.isArray(photo.standpoints) && photo.standpoints) || [];
  const primary = standpoints[0] || null;
  const agreement = Number((primary && primary.agreement) || 0);
  const answerCount = Number((photo && photo.answerCount) || 0);

  return {
    kind: 'detective',
    label:
      agreement > 1
        ? `${agreement} people put it here`
        : standpoints.length > 1
          ? 'One of several different answers'
          : 'One person put it here',
    detail:
      standpoints.length > 1
        ? `${standpoints.length} different places were proposed by ${answerCount} answers.`
        : '',
    correctionDistanceMeters: null,
    originalGps: null,
    selectedGps: getPhotoPosition(photo),
  };
};

export const AUTO_MODERATION_ACTIONS = Object.freeze([
  'assign_spot',
  'create_spot',
  'reject',
  'needs_more_info',
  'needs_review',
  'needs_gps_fix',
]);

// The three queues Auto Mod runs, in the order it runs them. User photos come
// first because somebody is waiting on the answer; Detective is the backlog of
// Unsplash photos the app's users have located; Unsplash is the far larger pile
// nobody has located, where the only thing saying where a photo was taken is the
// coordinate the camera wrote.
export const QUEUE = Object.freeze({
  USER_PHOTOS: 'user_photos',
  DETECTIVE: 'detective',
  UNSPLASH: 'unsplash',
  // The safe Unsplash judge. It only shows what it is sure of, and nothing it
  // says is applied without the owner approving it.
  UNSPLASH_SAFE: 'unsplash_safe',
});

// Both Unsplash queues can reject, with the same causes and through the same call
// Mod uses - but never for the coordinate, which is what needs_more_info is for.
// Must stay in sync with QUEUE_ACTIONS in the mod backend.
export const QUEUE_ACTIONS = Object.freeze({
  [QUEUE.USER_PHOTOS]: Object.freeze([
    'assign_spot',
    'create_spot',
    'reject',
    'needs_review',
    'needs_gps_fix',
  ]),
  [QUEUE.DETECTIVE]: Object.freeze([
    'assign_spot',
    'create_spot',
    'reject',
    'needs_more_info',
    'needs_review',
  ]),
  [QUEUE.UNSPLASH]: Object.freeze([
    'assign_spot',
    'create_spot',
    'reject',
    'needs_more_info',
    'needs_review',
  ]),
  [QUEUE.UNSPLASH_SAFE]: Object.freeze(['assign_spot', 'create_spot', 'needs_review']),
});

// Both queues that read the unsplash collection. Everything downstream that has
// to pick an endpoint asks this rather than testing for Detective, which is what
// used to send an Unsplash decision through the user-photo endpoints.
export const isUnsplashQueue = queue =>
  queue === QUEUE.DETECTIVE || queue === QUEUE.UNSPLASH || queue === QUEUE.UNSPLASH_SAFE;

export const getQueueActions = queue => QUEUE_ACTIONS[queue] || QUEUE_ACTIONS[QUEUE.USER_PHOTOS];

// Three queues run through one screen behind the same buttons, and the same
// decision means different things in each: a user photo has somebody waiting for
// it and can be turned down, a Detective photo was published long ago and
// carries other people's answers, an Unsplash photo has never been shown to
// anybody. Which one is on screen has to be readable without inferring it from
// the actions on offer, so it is named on the photo as well as in the bar.
const QUEUE_BADGE = Object.freeze({
  [QUEUE.USER_PHOTOS]: Object.freeze({
    label: 'User photos',
    description: 'Uploaded by our users · nothing happens without approval',
  }),
  [QUEUE.DETECTIVE]: Object.freeze({
    label: 'Detective',
    description: 'Located by the app’s users',
  }),
  [QUEUE.UNSPLASH]: Object.freeze({
    label: 'Unsplash',
    description: 'A geocoded location tag · a new spot has to stand on a POI',
  }),
  [QUEUE.UNSPLASH_SAFE]: Object.freeze({
    label: 'Unsplash · safe',
    description: 'Only what the judge is sure of · approve it if it got it right',
  }),
});

// The near pass is worth saying out loud: it changes what the answers look like,
// because these are the photos an existing spot can absorb.
const UNSPLASH_NEAR_BADGE = Object.freeze({
  label: 'Unsplash · near a spot',
  description: 'Within 500 m of a spot that already exists · most of these get assigned',
});

export const getQueueBadge = (queue, { nearSpots = false } = {}) => {
  if (queue === QUEUE.UNSPLASH && nearSpots) {
    return UNSPLASH_NEAR_BADGE;
  }

  return QUEUE_BADGE[queue] || QUEUE_BADGE[QUEUE.USER_PHOTOS];
};

// Both leave the photo pending for Mod, and neither applies anything. They are
// still different answers: needs_review is the model unable to judge the photo,
// needs_gps_fix is the model judging it and handing over a job that needs Street
// View. Nothing is approved or created by either.
export const ABSTAIN_ACTIONS = Object.freeze(['needs_review', 'needs_gps_fix']);

export const isAbstention = action => ABSTAIN_ACTIONS.includes(action);

// Must stay in sync with REJECTION_CAUSES in the mod backend. Three of the causes
// available on /moderation are absent on purpose: "Drone" (drone photos are
// allowed), "Too many photos from that spot" (only ever used by hand) and
// "Already approved" (perfect duplicates are caught at upload).
export const REJECTION_CAUSES = Object.freeze([
  { value: 0, label: 'Wrong GPS' },
  { value: 1, label: 'Bad quality' },
  { value: 4, label: 'Private' },
  { value: 5, label: 'Selfie' },
  { value: 6, label: 'Temporary' },
  { value: 8, label: 'Not a spot' },
  { value: 10, label: 'Not interesting enough' },
  { value: 11, label: 'Not centered' },
  { value: 12, label: 'Too panoramic' },
  { value: 13, label: 'Too edited' },
  { value: 14, label: 'Border too big' },
  { value: 15, label: 'Watermark too big' },
]);

// A rejected suggestion only leaves the run it was rejected in. Reloading the
// queue picks the photo up again and the backend proposes something new for it.
// An accepted abstention stays out: that photo was already routed to /moderation.
const SESSION_EXCLUDED_STATES = new Set(['accepted']);
const VALID_REJECTION_CAUSES = new Set(REJECTION_CAUSES.map(cause => cause.value));

const normalizeState = value => String(value == null ? '' : value).trim().toLowerCase();

export const getAutoModerationState = photo => {
  const autoModeration = (photo && photo.autoModeration) || {};
  const review = autoModeration.review || autoModeration.latestReview || {};
  const values = [
    autoModeration.state,
    autoModeration.status,
    autoModeration.outcome,
    review.state,
    review.status,
    review.outcome,
  ];

  return values.map(normalizeState).find(Boolean) || '';
};

// An appealed photo was already rejected once and the uploader wrote why they
// disagree. That text is theirs, not evidence, and it never goes near the model:
// the appeal is read and decided by hand in /moderation.
export const hasAppeal = photo =>
  Boolean(photo && photo.appeal && String(photo.appeal.text || '').trim());

export const shouldIncludeInAutoModerationSession = photo =>
  !hasAppeal(photo) && !SESSION_EXCLUDED_STATES.has(getAutoModerationState(photo));

const hasText = value => Boolean(String(value == null ? '' : value).trim());

const isValidCoordinate = (value, minimum, maximum) => {
  if (value === null || typeof value === 'undefined' || String(value).trim() === '') {
    return false;
  }

  const number = Number(value);
  return Number.isFinite(number) && number >= minimum && number <= maximum;
};

export const getRejectionCauseLabel = cause => {
  const causeNumber = Number(cause);
  const match = REJECTION_CAUSES.find(item => item.value === causeNumber);
  return match ? match.label : 'Unknown';
};

export const getSuggestionValidationError = (suggestion, photo) => {
  if (!suggestion || typeof suggestion !== 'object') {
    return 'The model did not return a suggestion.';
  }

  if (!hasText(suggestion.id)) {
    return 'The suggestion is missing its audit id.';
  }

  if (!photo || String(suggestion.photoId) !== String(photo.id)) {
    return 'The suggestion belongs to a different photo.';
  }

  // A suggestion built for another queue would be applied through the wrong
  // endpoints against the wrong collection.
  const queue = QUEUE_ACTIONS[photo.queue] ? photo.queue : QUEUE.USER_PHOTOS;

  if (suggestion.queue && suggestion.queue !== queue) {
    return 'The suggestion was built for a different moderation queue.';
  }

  if (!getQueueActions(queue).includes(suggestion.action)) {
    return 'The suggestion returned an unsupported action.';
  }

  if (suggestion.action === 'assign_spot' && !hasText(suggestion.targetSpot?.id)) {
    return 'The assignment suggestion is missing its target spot.';
  }

  if (suggestion.action === 'create_spot') {
    const spot = suggestion.newSpot;

    if (!spot || typeof spot !== 'object') {
      return 'The new-spot suggestion is missing its exact fields.';
    }

    if (!hasText(spot.name) || !hasText(spot.country) || !hasText(spot.slug)) {
      return 'A new spot requires a name, country, and slug.';
    }

    if (!isValidCoordinate(spot.lat, -90, 90) || !isValidCoordinate(spot.lng, -180, 180)) {
      return 'The new spot has invalid coordinates.';
    }
  }

  if (suggestion.action === 'reject') {
    const cause = Number(suggestion.rejection?.cause);

    if (!Number.isFinite(cause) || !VALID_REJECTION_CAUSES.has(cause)) {
      return 'The rejection suggestion has an unsupported cause.';
    }
  }

  return null;
};

export const getApprovalBlockReason = (suggestion, photo) => {
  return getSuggestionValidationError(suggestion, photo);
};

// The slug is derived from name, from, city and country, so a spot already
// holding the proposed slug is the same place under the same name: the photo
// belongs to it. This only happens mid-session - the backend refuses a taken
// slug at generation time - so it means a sibling photo created the spot after
// this suggestion was built, and the right answer is to assign rather than to
// fail. A different vantage would have carried a different "from" and so a
// different slug.
export const findSpotOwningSlug = (spots, slug) => {
  const wanted = String(slug || '').trim().toLocaleLowerCase();

  if (!wanted || !Array.isArray(spots)) {
    return null;
  }

  return (
    spots.find(spot => String((spot && spot.slug) || '').trim().toLocaleLowerCase() === wanted) ||
    null
  );
};

export const getSlugCollisionSpot = (suggestion, spots) =>
  suggestion && suggestion.action === 'create_spot' && suggestion.newSpot
    ? findSpotOwningSlug(spots, suggestion.newSpot.slug)
    : null;

export const INITIAL_CORRECTION = Object.freeze({
  action: '',
  spotId: '',
  name: '',
  from: '',
  cause: '',
  note: '',
});

// The half of a correction that can be measured later: not only that the model
// was wrong, but what the right decision was.
export const getCorrectionPayload = (correction = INITIAL_CORRECTION) => {
  const action = String(correction.action || '').trim();
  const payload = { action, note: String(correction.note || '').trim() };

  if (action === 'assign_spot') {
    payload.spotId = String(correction.spotId || '').trim();
  }

  if (action === 'create_spot') {
    payload.name = String(correction.name || '').trim();
    payload.from = String(correction.from || '').trim();
  }

  if (action === 'reject') {
    payload.cause = Number(correction.cause);
  }

  return payload;
};

export const getCorrectionError = (payload = {}) => {
  if (!AUTO_MODERATION_ACTIONS.includes(payload.action)) {
    return 'Say what the right decision would have been before continuing.';
  }

  if (payload.action === 'assign_spot' && !hasText(payload.spotId)) {
    return 'Pick the spot the photo should have been assigned to.';
  }

  if (payload.action === 'create_spot' && !hasText(payload.name)) {
    return 'Write the name the new spot should have had.';
  }

  if (payload.action === 'reject' && !VALID_REJECTION_CAUSES.has(Number(payload.cause))) {
    return 'Pick the rejection cause that should have been used.';
  }

  return null;
};

// The model is handed image urls and downloads them itself, so a photo whose
// file has gone from its CDN comes back as a 400 carrying an upstream 404.
// Retrying asks the same dead url again: the file is not coming back. Unsplash's
// older photos do this often enough that a screen the owner has to dismiss is
// pure interruption, so the queue moves on by itself.
export const isMissingImageError = error => {
  const message = String((error && error.message) || '');

  return (
    (error && error.code) === 'AUTO_MODERATION_IMAGE_MISSING' ||
    message.includes('Error while downloading file') ||
    /Upstream status code: 4\d\d/.test(message)
  );
};

export const isTerminalSuggestionError = error => {
  const status = Number(error && error.status);
  const code = String((error && error.code) || '');

  return status === 404 || (status === 409 && !code.startsWith('AUTO_MODERATION_GENERATION_'));
};

const compactFinalization = finalization => {
  if (!finalization || typeof finalization !== 'object') {
    return undefined;
  }

  const compact = {
    counted: Boolean(finalization.counted),
    decisionType: finalization.decisionType,
    approvalSpotId: finalization.approvalSpotId,
    rejectionCause: finalization.rejectionCause,
    sequence: finalization.sequence,
    finalizedAt: finalization.finalizedAt,
  };

  return Object.fromEntries(
    Object.entries(compact).filter(([, value]) => typeof value !== 'undefined')
  );
};

export const buildCompactApplyResult = ({ suggestion, result }) => {
  const finalization = compactFinalization(result && result.moderationFinalization);
  const applyResult = {
    action: suggestion.action,
    ...(finalization ? { moderationFinalization: finalization } : {}),
  };

  if (suggestion.action === 'assign_spot') {
    applyResult.spotId = String(suggestion.targetSpot.id);
  } else if (suggestion.action === 'create_spot') {
    const createdSpotId = result && result.id;

    if (createdSpotId) {
      applyResult.spotId = String(createdSpotId);
    }

    applyResult.slug = String(suggestion.newSpot.slug);
  } else if (suggestion.action === 'reject') {
    applyResult.rejectionCause = Number(suggestion.rejection.cause);
  }

  return applyResult;
};
